import { canPerform } from './canPerform'
import type { ChannelAction } from './graphqlEnums'

type ChannelSubject = {
  allowedActions?: readonly ChannelAction[] | null
}

/**
 * A channel is **view-only for Publish** when the actor can open the Publish
 * surface (queue/calendar) but cannot create, edit, schedule, or delete posts
 * on it. The Publish analogue of Community's `isViewOnlyChannel`.
 *
 * `manageUpdates` is granted for the `fullPosting` and `requiresApproval`
 * publish tiers and denied for `viewOnly`, so approval-flow members are NOT
 * view-only — they can still create posts (pending approval).
 */
export const isViewOnlyPublishChannel = (
  channel: ChannelSubject | null | undefined,
): boolean =>
  canPerform(channel, 'viewPublish') && !canPerform(channel, 'manageUpdates')

export const VIEW_ONLY_PUBLISH_CHANNEL_TOOLTIP =
  'You have view-only access in Publish to this channel.'

export function getViewOnlyPublishChannelIds<
  T extends ChannelSubject & { id: string },
>(channels: readonly T[]): Set<string> {
  return new Set(
    channels
      .filter((channel) => isViewOnlyPublishChannel(channel))
      .map((channel) => channel.id),
  )
}

/**
 * Whether Publish should treat a whole channel set as view-only: the actor
 * can view at least one channel but can create posts on none of them
 * (view-only access, or a mix of view-only and no access).
 *
 * `false` for an empty set and when nothing is viewable: those states render
 * their own empty/access-denied treatments, not the view-only one.
 */
export const areAllChannelsViewOnlyForPublish = (
  channels: readonly ChannelSubject[],
): boolean =>
  channels.some((channel) => canPerform(channel, 'viewPublish')) &&
  !channels.some((channel) => canPerform(channel, 'manageUpdates'))

/**
 * Whether Publish is fully restricted for the actor: the organization has
 * channels, but the actor can view none of them in Publish. This is the
 * trigger for the full-page Access Restricted state — distinct
 * from view-only (can see the queue/calendar) and from an organization with
 * no channels at all (which keeps its connect-a-channel state).
 */
export const areAllChannelsRestrictedForPublish = (
  channels: readonly ChannelSubject[],
): boolean =>
  channels.length > 0 &&
  !channels.some((channel) => canPerform(channel, 'viewPublish'))

/**
 * The actor's Publish access shape across a channel set, used by the
 * view-only discovery coach marks to decide which education to
 * show. The values are mutually exclusive, which is what guarantees an actor
 * sees at most one of the two coach marks:
 *
 * - `allViewOnly` — every channel the actor can view is view-only (channels
 *   the actor cannot view at all don't count, matching
 *   {@link areAllChannelsViewOnlyForPublish}).
 * - `mixed` — at least one view-only channel alongside at least one the
 *   actor can post to (full or approval access).
 * - `fullAccess` — the actor can post somewhere and nothing is view-only.
 * - `none` — no viewable channels (empty set or access-denied states).
 */
export type PublishAccessScope = 'none' | 'allViewOnly' | 'mixed' | 'fullAccess'

export const getPublishAccessScope = (
  channels: readonly ChannelSubject[],
): PublishAccessScope => {
  const hasViewOnlyChannel = channels.some(isViewOnlyPublishChannel)
  const hasPostingChannel = channels.some((channel) =>
    canPerform(channel, 'manageUpdates'),
  )

  if (hasViewOnlyChannel && hasPostingChannel) {
    return 'mixed'
  }
  if (hasViewOnlyChannel) {
    return 'allViewOnly'
  }
  if (hasPostingChannel) {
    return 'fullAccess'
  }
  return 'none'
}
